
import React, { useState, useEffect } from 'react';
import AiPrayerForm from './AiPrayerForm';
import { useAiPrayerAssistantForm } from '@/hooks/useAiPrayerAssistantForm';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { useCommentsDialog } from '@/hooks/useCommentsDialog';
import { CommentsDialog } from '@/components/comments';
import GeneratedPrayerDisplay from './components/GeneratedPrayerDisplay';
import { StandardDialogTemplate } from '@/components/shared/StandardDialogTemplate';

interface AiPrayerAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AiPrayerAssistantModal({
  isOpen,
  onClose
}: AiPrayerAssistantModalProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { 
    generatedPrayer, 
    resetForm, 
    formData, 
    updateFormData, 
    formSettings, 
    submitRequest 
  } = useAiPrayerAssistantForm();
  const { stop } = useAbielAudio();
  const [prayerKey, setPrayerKey] = useState(0);
  const { isCommentsOpen, selectedPrayerForComments, openComments, closeComments } = useCommentsDialog();

  // Enhanced cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.debug('[AiPrayerAssistantModal] Modal closed - stopping audio');
      stop();
    }
  }, [isOpen, stop]);

  // Force audio system refresh when generatedPrayer changes
  useEffect(() => {
    if (generatedPrayer) {
      console.log('[DEBUG] Generated prayer updated, forcing audio refresh');
      // Stop any existing audio
      stop();
      // Force re-render of prayer object with new key
      setPrayerKey(prev => prev + 1);
    }
  }, [generatedPrayer, stop]);

  // Enhanced close handler with audio cleanup
  const handleClose = () => {
    console.debug('[AiPrayerAssistantModal] Handling close - stopping audio');
    stop();
    onClose();
  };

  // Toggle handler for expand/collapse
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle back arrow click
  const handleBackClick = () => {
    handleClose();
  };

  // Gesture callbacks for StandardDialogTemplate
  const gestureCallbacks = {
    onExpand: () => setIsExpanded(true),
    onCollapse: () => setIsExpanded(false),
    onClose: handleClose,
  };

  // Comment handler for AI generated prayers
  const handleComment = (prayer: any) => {
    openComments(prayer);
  };

  if (!isOpen) return null;

  // Header configuration for StandardDialogTemplate
  const headerConfig = {
    variant: 'primary' as const,
    isExpanded,
    onToggle: handleToggleExpanded,
    onClose: handleClose,
    onBackClick: handleBackClick,
    customLeftSection: (
      <div className="text-gold font-medium text-center">
        Abiel AI
      </div>
    ),
    customRightSection: (
      <div className="text-gold/80 text-sm text-center">
        Pray
      </div>
    ),
  };

  return (
    <>
      {generatedPrayer ? (
        <GeneratedPrayerDisplay 
          prayer={generatedPrayer} 
          onNewPrayer={() => {
            // Reset form and keep modal open
            resetForm();
            setPrayerKey(prev => prev + 1);
          }}
          onClose={handleClose}
          onComment={handleComment}
        />
      ) : (
        <StandardDialogTemplate
          isOpen={isOpen}
          isExpanded={isExpanded}
          onClose={handleClose}
          onToggleExpanded={handleToggleExpanded}
          customZIndex={82}
          headerConfig={headerConfig}
          gesturePreset="expand-collapse"
          gestureCallbacks={gestureCallbacks}
          showHints={true}
          backdropConfig={{
            enabled: true,
            oppositeTheme: true,
            onBackdropClick: handleClose,
          }}
          ariaLabel="AI Prayer Assistant"
        >
          <AiPrayerForm 
            onClose={handleClose} 
            onComment={handleComment}
            formData={formData}
            updateFormData={updateFormData}
            formSettings={formSettings}
            submitRequest={submitRequest}
          />
        </StandardDialogTemplate>
      )}

      {/* Comments Dialog for AI generated prayers */}
      {selectedPrayerForComments && (
        <CommentsDialog
          isOpen={isCommentsOpen}
          onClose={closeComments}
          prayer={selectedPrayerForComments}
          currentUserId={undefined} // TODO: Add currentUserId
        />
      )}
    </>
  );
}
